<template>
  <div class="relative flex flex-col h-screen">
    <img :src="getImage('mascot')" class="fixed papsi-mascot" />

    <div class="flex flex-1 lg:flex-row flex-col">
      <SidebarSection>
        <LabelControl type="bubble" :content="$t('led016')" />
      </SidebarSection>

      <ContentSection :show_back="!this.loading_transactions" :show_logout="!this.loading_transactions"
        @logout="logout">
        <div v-if="this.loading_transactions" style="height: 55px"></div>
        <div class="flex flex-row justify-between items-center mb-10">
          <div class="flex flex-col">
            <LabelControl type="heading" :content="displayDepositType" />
            <p v-if="!this.loading_transactions" class="text-2xl text-grey-dark italic mt-2">
              {{ $t("led002") }} {{ this.date }}
            </p>
          </div>

          <div class="flex flex-row rounded-full bg-grey-lighter text-3xl" v-if="summaryEnabled">
            <div :class="[
              'flex justify-center align-middle items-center w-48 ml-4 my-4 px-4 py-3 rounded-full',
              this.current_tab === 'all'
                ? 'bg-white font-medium text-grey-darkest'
                : 'text-grey-dark cursor-pointer',
            ]" @click="setTab('all')">
              {{ $t("btn006") }}
            </div>

            <div :class="[
              'flex justify-center align-middle items-center w-48 mr-4 my-4 px-4 py-3 rounded-full',
              !this.loading_transactions && this.transactions.length > 0
                ? this.current_tab === 'summary'
                  ? 'bg-white font-medium text-grey-darkest'
                  : 'text-grey-dark cursor-pointer'
                : 'text-grey-light cursor-not-allowed',
            ]" @click="
              !this.loading_transactions &&
              this.transactions.length > 0 &&
              setTab('summary')
              ">
              {{ $t("btn007") }}
            </div>
          </div>
        </div>

        <DataTableSkeletonControl v-if="loading_transactions" />

        <div v-else>
          <DataTableControl v-if="current_tab === 'all'" :display_style="['SAV', 'RSD', 'SSD', 'SHR'].includes(deposit_code)
            ? 'savings'
            : 'loans'
            " :table_headers="allTableHeaders" :table_data="transactions"
            :show_all_transactions="this.deposit_code === 'SHR'" />

          <DataTableControl v-else-if="current_tab === 'summary' && show_zero_balance" :show_all_transactions="true"
            :table_headers="extractHeaders(grouped_transactions)" :table_data="grouped_transactions"
            :show_row_button="true" :show_transaction_summary="true" @rowclick="handleRowClick" />

          <DataTableControl v-else-if="current_tab === 'summary' && !show_zero_balance" :show_all_transactions="true"
            :table_headers="extractHeaders(nonzero_transactions)" :table_data="nonzero_transactions"
            :show_row_button="true" :show_transaction_summary="true" @rowclick="handleRowClick" />

          <transition name="slide-in">
            <ModalControl :is_table="true" :is_visible="is_modal_open" class="p-2">
              <div class="relative">
                <div class="p-5 mb-5">
                  <div class="flex flex-col">
                    <div class="flex flex-row items-center justify-between">
                      <div class="flex flex-row items-center justify-start">
                        <p class="text-primary uppercase text-5xl font-bold">
                          {{ displayDepositType }}
                        </p>
                        <p v-if="
                          getTracker &&
                          displayDepositType != 'SPECIAL SAVINGS DEPOSIT'
                        " :class="{
                          'bg-primary': isFullyPaid,
                          'bg-error': !isFullyPaid,
                          'rounded-full text-white text-xl px-5 py-3 font-semibold tracker ml-5': true,
                        }">
                          {{ getTracker }}
                        </p>
                      </div>

                      <IconControl @click="closeModal" id="exit-black" :width="50" :height="50" />
                    </div>
                    <div class="flex flex-row items-center justify-between mt-5 text-grey-darker text-3xl">
                      <div class="flex flex-row">
                        <p class="capitalize">
                          <b>Reference: </b> {{ current_reference }}
                        </p>
                        <p class="ml-10" style="bottom: 1rem; right: 1rem" v-if="
                          $store.state.ledgers.types.find(
                            (item) => item.code === deposit_code,
                          ).terms[getLoanTerms]
                        ">
                          <b>Terms:</b>
                          {{
                            toTitleCase(
                              $store.state.ledgers.types.find(
                                (item) => item.code === deposit_code,
                              ).terms[getLoanTerms],
                            )
                          }}
                        </p>
                      </div>
                      <p>
                        <b>Balance:</b>
                        {{ formatAmount(getCurrentBalance) }}
                      </p>
                    </div>
                  </div>
                </div>
                <DataTableControl :show_result_count="false" :table_headers="modalTableHeaders"
                  :table_data="filtered_transactions" display_style="loans" :display_limit="6"
                  :show_all_transactions="true" />
              </div>
            </ModalControl>
          </transition>

          <div v-if="current_tab === 'summary' && toggleHideZeroBalances" @click="toggleShowNonzero()"
            class="fixed flex justify-end align-middle papsi-toggle mt-4">
            <div v-if="show_zero_balance" class="flex flex-row bg-grey-dark rounded-full px-5 py-3 justify-end w-auto">
              <p class="text-xl text-white">{{ $t("led005") }}</p>
              <img :src="getIcon('hide')" class="ml-2" />
            </div>
            <div v-else class="flex flex-row bg-primary rounded-full px-5 py-3 justify-end w-auto">
              <p class="text-xl text-white">{{ $t("led006") }}</p>
              <img :src="getIcon('show')" class="ml-2" />
            </div>
          </div>
        </div>

        <!-- Existing Session -->
        <transition name="slide-in">
          <ModalControl :is_visible="existing_session">
            <SessionTimeoutControl />
          </ModalControl>
        </transition>

        <div v-if="
          !this.loading_transactions &&
          transactions.length > 0 &&
          this.deposit_type != 'shares'
        " class="flex flex-row text-xl justify-end mt-1">
          <div class="flex flex-row items-center">
            <div class="bg-primary w-4 h-4 rounded-full"></div>
            <div class="ml-4 text-primary font-bold" style="margin-top: 1px">
              <p v-if="this.deposit_type == 'savings'">Credit/Deposit</p>
              <p v-else-if="this.deposit_type == 'loans'">Debit</p>
            </div>
          </div>
          <div class="flex flex-row items-center">
            <div class="ml-8 bg-error w-4 h-4 rounded-full"></div>
            <div class="ml-4 text-error font-bold" style="margin-top: 1px">
              <p v-if="this.deposit_type == 'loans'">Credit</p>
              <p v-else-if="this.deposit_type == 'savings'">Debit/Withdrawal</p>
            </div>
          </div>
        </div>
      </ContentSection>
    </div>
    <FooterSection />
  </div>
</template>

<script>
import SidebarSection from "@/components/sections/SidebarSection.vue";
import ContentSection from "@/components/sections/ContentSection.vue";
import FooterSection from "@/components/sections/FooterSection.vue";
import LabelControl from "@/components/controls/LabelControl.vue";
import DataTableControl from "@/components/controls/DataTableControl.vue";
import DataTableSkeletonControl from "@/components/controls/DataTableSkeletonControl.vue";
import ModalControl from "@/components/controls/ModalControl.vue";
import IconControl from "@/components/controls/IconControl.vue";
import SessionTimeoutControl from "@/components/controls/SessionTimeoutControl.vue";
import user from "@/services/user";
import mixin from "@/mixins/controls.js";

export default {
  mixins: [mixin],
  inject: ["setLoading", "setError"],
  components: {
    SidebarSection,
    ContentSection,
    FooterSection,
    LabelControl,
    DataTableControl,
    DataTableSkeletonControl,
    ModalControl,
    SessionTimeoutControl,
    IconControl,
  },
  mounted() {
    this.$emit("checkauth");
    this.loading_transactions = true;
    if (this.getTransactionsByCode) {
      this.transactions = this.getTransactionsByCode;

      this.date =
        this.transactions.length > 0
          ? this.formatDate(this.transactions[0].date)
          : "";

      this.loading_transactions = false;
    } else {
      this.createRequest();
    }
  },
  data() {
    return {
      loading_transactions: true,
      balance_visible: false,
      deposit_code: this.$route.params.code,
      deposit_type: this.$route.params.type,
      current_balance: null,
      transactions: [],
      grouped_transactions: [],
      filtered_transactions: [],
      nonzero_transactions: [],
      show_zero_balance: true,
      is_modal_open: false,
      is_main_table: true,
      current_reference: "",
      existing_session: false,
      timestamp: "",
      date: "",
      time: "",
      reference: "",
      ledger_delay: parseInt(process.env.VUE_APP_LEDGER_FETCH_DELAY, 10),
      current_tab: "all",
    };
  },
  computed: {
    modalTableHeaders() {
      if (this.displayDepositType === "SPECIAL SAVINGS DEPOSIT") {
        return [
          "date",
          "description",
          "mature_date",
          "debit",
          "credit",
          "balance",
        ];
      }

      return ["date", "description", "debit", "credit", "balance"];
    },
    allTableHeaders() {
      if (this.displayDepositType === "SPECIAL SAVINGS DEPOSIT") {
        return [
          "date",
          "description",
          "mature_date",
          "debit",
          "credit",
          "balance",
        ];
      }

      return ["date", "description", "debit", "credit", "balance"];
    },
    summaryEnabled() {
      const ledgerType = this.$store.state.ledgers.types.find(
        (type) => type.code === this.deposit_code,
      );
      return ledgerType
        ? ledgerType.is_loan || this.deposit_code === "SSD"
        : false;
    },
    toggleHideZeroBalances() {
      const hasZeroBalance = this.grouped_transactions.some(
        (transaction) => transaction.balance_per_id == 0,
      );
      return (
        this.deposit_type === "loans" &&
        this.grouped_transactions.length > 1 &&
        hasZeroBalance
      );
    },
    displayDepositType() {
      const item = this.$store.state.ledgers.types.find(
        (item) => item.code === this.deposit_code,
      );
      return item ? item.name : null;
    },
    getTransactionsByCode() {
      const item = this.$store.state.user.ledgers.find(
        (item) => item.code === this.deposit_code,
      );
      return item && item.is_retrieved ? item.transactions : null;
    },
    getTimestamp() {
      console.log("ledger: ", this.$store.state.user.ledgers);
      const item = this.$store.state.user.ledgers.find(
        (item) => item.code === this.deposit_code,
      );
      return item ? this.formatDate(item.as_of) : null;
    },
    getLoanTerms() {
      return this.filtered_transactions.length > 0
        ? this.filtered_transactions[0].terms
        : "N/A";
    },
    getCurrentBalance() {
      return this.filtered_transactions.length > 0
        ? this.filtered_transactions[0].balance
        : "N/A";
    },
    getTracker() {
      const summary = this.grouped_transactions.find(
        (item) => item.reference === this.current_reference,
      );

      return summary ? summary.tracking : "N/A";
    },
    isFullyPaid() {
      const summary = this.grouped_transactions.find(
        (item) => item.reference === this.current_reference,
      );

      if (!summary) return false;

      const match = summary.tracking.match(/^(\d+)\s+of\s+(\d+)$/);

      if (!match) return false;

      const completed = parseInt(match[1], 10);
      const total = parseInt(match[2], 10);

      return completed >= total;
    },
  },
  methods: {
    logout() {
      this.$emit("logout");
    },
    // formatTimestamp() {
    //   if (this.timestamp) {
    //     // this.date = this.formatDate(this.timestamp.split(" ")[0]);
    //     // this.time = this.formatTime(this.timestamp.split(" ")[1]);
    //     this.date =
    //   }
    // },
    setTab(tab) {
      if (this.current_tab === tab) return;
      this.current_tab = tab;
      if (tab === "summary" && !this.grouped_transactions.length) {
        this.groupLoans();
      }
    },
    toggleShowNonzero() {
      if (this.show_zero_balance) {
        this.filterNonzeroTransactions();
        this.show_zero_balance = false;
      } else {
        this.show_zero_balance = true;
      }
    },
    filterNonzeroTransactions() {
      this.nonzero_transactions = this.grouped_transactions.filter(
        (transaction) => transaction.balance_per_id != "0.00",
      );
    },
    computeRunningBalance(reference) {
      if (this.is_main_table) {
        this.filtered_transactions = this.transactions
          .filter((transaction) => {
            const id =
              this.deposit_code === "SSD"
                ? transaction.reference_id || ""
                : transaction.loan_id;

            return reference === id;
          })
          .map(
            ({
              date,
              description,
              mature_date,
              debit,
              credit,
              terms,
              balance,
            }) => {
              this.current_reference = reference; // or row.reference in handleRowClick

              return {
                date,
                description,
                mature_date,
                debit: parseFloat(debit).toFixed(2),
                credit: parseFloat(credit).toFixed(2),
                balance,
                terms,
              };
            },
          );

        if (this.deposit_code === "SSD") {
          let current_balance = 0.0;
          for (let i = this.filtered_transactions.length - 1; i >= 0; i--) {
            const transaction = this.filtered_transactions[i];
            if (parseFloat(transaction.debit) > 0) {
              current_balance -= parseFloat(transaction.debit);
            } else if (parseFloat(transaction.credit) > 0) {
              current_balance += parseFloat(transaction.credit);
            }
            transaction.balance = parseFloat(current_balance).toFixed(2);
          }
          return parseFloat(current_balance).toFixed(2);
        }
      }
    },
    handleRowClick(row) {
      if (this.current_tab === "summary") {
        this.filtered_transactions = this.transactions
          .filter((transaction) => {
            const id =
              this.deposit_code === "SSD"
                ? transaction.reference_id || ""
                : transaction.loan_id;

            return row.reference === id;
          })
          .map(
            ({
              date,
              description,
              mature_date,
              debit,
              credit,
              terms,
              balance,
            }) => {
              this.current_reference = row.reference; // or row.reference in handleRowClick

              return {
                date,
                description,
                mature_date,
                debit: parseFloat(debit).toFixed(2),
                credit: parseFloat(credit).toFixed(2),
                balance,
                terms,
              };
            },
          );

        if (this.deposit_code === "D44") {
          let current_balance = 0.0;
          for (let i = this.filtered_transactions.length - 1; i >= 0; i--) {
            const transaction = this.filtered_transactions[i];
            if (parseFloat(transaction.debit) > 0) {
              current_balance += parseFloat(transaction.debit);
            } else if (parseFloat(transaction.credit) > 0) {
              current_balance -= parseFloat(transaction.credit);
            }
            transaction.balance = parseFloat(current_balance).toFixed(2);
          }
        } else if (this.deposit_code === "SSD") {
          let current_balance = 0.0;
          for (let i = this.filtered_transactions.length - 1; i >= 0; i--) {
            const transaction = this.filtered_transactions[i];
            if (parseFloat(transaction.debit) > 0) {
              current_balance -= parseFloat(transaction.debit);
            } else if (parseFloat(transaction.credit) > 0) {
              current_balance += parseFloat(transaction.credit);
            }
            transaction.balance = parseFloat(current_balance).toFixed(2);
          }
        }
        this.is_modal_open = true;
        this.is_main_table = false;
      }
    },
    closeModal() {
      this.is_modal_open = false;
      this.is_main_table = true;
    },
    createRequest() {
      this.loading_transactions = true;
      user
        .createTransactionRequest(
          this.$store.state.user.hash,
          this.deposit_code,
          this.$store.state.user.uuid,
          this.$store.state.user.key,
        )
        .then((response) => {
          this.reference = response.data.data.reference;
          this.transactions = response.data.data;
          this.timestamp = response.data.timestamp;
          this.date = this.formatDate(this.timestamp);
          // this.formatTimestamp();
          this.transactions.reverse();

          this.date =
            this.transactions.length > 0
              ? this.formatDate(this.transactions[0].date)
              : "";

          this.$store.commit("addTransaction", {
            code: this.deposit_code,
            transactions: this.transactions,
            timestamp: this.timestamp,
            is_retrieved: true,
          });

          if (this.transactions.length > 0) {
            this.$store.commit("addBalance", {
              code: this.deposit_code,
              balance: this.transactions[0].balance,
            });
          }
          this.groupLoans();
          this.has_fetched_transactions = true;
          this.loading_transactions = false;
        })
        .catch((error) => {
          if (error.response && error.response.status === 403) {
            this.setLoading(true);
            this.resetUserData();
            this.clearUserCookie();
            this.toggleExistingSessionModal();
            this.setLoading(false);
          }
          this.loading_transactions = false;
        });
    },
    fetchTransactions() {
      user
        .fetchTransactionRequest(
          this.$store.state.user.hash,
          this.reference,
          this.deposit_code,
          this.$store.state.user.uuid,
          this.$store.state.user.key,
        )
        .then((response) => {
          this.transactions = response.data.data;
          this.timestamp = response.data.timestamp;
          this.date = this.formatDate(this.timestamp);
          // this.formatTimestamp();
          this.transactions.reverse();
          this.date =
            this.transactions.length > 0
              ? this.formatDate(this.transactions[0].date)
              : "";
          this.$store.commit("addTransaction", {
            code: this.deposit_code,
            transactions: this.transactions,
            timestamp: this.timestamp,
            is_retrieved: true,
          });
          if (this.transactions.length > 0) {
            this.$store.commit("addBalance", {
              code: this.deposit_code,
              balance: this.transactions[0].balance,
            });
          }
          this.groupLoans();
          this.has_fetched_transactions = true;
          this.loading_transactions = false;
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            this.showInfo("No transactions found");
            this.$store.commit("addTransaction", {
              code: this.deposit_code,
              transactions: [],
              timestamp: null,
              is_retrieved: true,
            });
            this.groupLoans();
            this.has_fetched_transactions = true;
            this.loading_transactions = false;
          } else if (error.response && error.response.status === 417) {
            let timer = setTimeout(() => {
              this.fetchTransactions();
              clearTimeout(timer);
            }, this.ledger_delay);
          } else if (error.response && error.response.status === 403) {
            this.setLoading(true);
            this.resetUserData();
            this.clearUserCookie();
            this.toggleExistingSessionModal();
            this.setLoading(false);
          } else {
            this.showError(
              "Failed to retrieve transactions. Please log in again.",
            );
            this.logout();
          }
        });
    },
    getRemainingBalance(total_debit, total_credit) {
      const balance = total_debit - total_credit;
      return balance < 1 ? "0.00" : balance.toFixed(2);
    },
    groupLoans() {
      const isSSD = this.deposit_code === "SSD";

      const groups = {};

      this.transactions.forEach(transaction => {
        const id = isSSD
          ? (transaction.reference_id || "")
          : (transaction.loan_id || "");

        if (!groups[id]) {
          groups[id] = [];
        }

        groups[id].push(transaction);
      });

      const transaction_summary = Object.entries(groups).map(([groupId, subledger]) => {
        // newest -> oldest
        const latestTransaction = subledger[0];
        const oldestTransaction = subledger[subledger.length - 1];

        let runningBalance = 0;

        // Build the same running balances shown in the modal
        for (let i = subledger.length - 1; i >= 0; i--) {
          const row = subledger[i];

          if (this.deposit_code === "SSD") {
            if (parseFloat(row.debit) > 0) {
              runningBalance -= parseFloat(row.debit || 0);
            } else {
              runningBalance += parseFloat(row.credit || 0);
            }
          } else if (this.deposit_code === "D44") {
            if (parseFloat(row.debit) > 0) {
              runningBalance += parseFloat(row.debit || 0);
            } else {
              runningBalance -= parseFloat(row.credit || 0);
            }
          } else {
            if (parseFloat(row.debit) > 0) {
              runningBalance += parseFloat(row.debit || 0);
            } else {
              runningBalance -= parseFloat(row.credit || 0);
            }
          }

          row.running_balance = runningBalance;
        }

        // First running balance (oldest row)
        const gross = subledger[subledger.length - 1].running_balance;

        // Current running balance (newest row)
        const balancePerId = subledger[0].running_balance;

        const termStr =
        latestTransaction.term || latestTransaction.terms || "";

        const grossAmount = parseFloat(gross);
        const loanBalance = parseFloat(balancePerId);

        // Special case for O0 loans
        if (
            termStr.startsWith("O0") &&
            grossAmount !== 0 &&
            loanBalance !== 0
        ) {
            return {
                reference: groupId,
                date: oldestTransaction.date,
                tracking: "0 of 1",
                gross: grossAmount.toFixed(2),
                balance_per_id: loanBalance.toFixed(2),
            };
        }

        let term = 1;

        if (termStr.startsWith("M")) {
            term = parseInt(termStr.substring(1), 10) || 1;
        } else if (termStr.startsWith("O")) {
            term = 1;
        } else {
            term = parseInt(termStr, 10) || 1;
        }

        const monthlyAmortization = grossAmount / term;

        const totalCredit = subledger.reduce(
            (sum, t) => sum + parseFloat(t.credit || 0),
            0
        );

        const completedTerms =
            monthlyAmortization > 0
                ? Math.floor(totalCredit / monthlyAmortization + 0.00001)
                : 0;

        return {
            reference: groupId,
            date: oldestTransaction.date,
            tracking: `${completedTerms} of ${term}`,
            gross: grossAmount.toFixed(2),
            balance_per_id: loanBalance.toFixed(2),
        };
      });

      transaction_summary.sort((a, b) => new Date(b.date) - new Date(a.date));

      this.grouped_transactions = transaction_summary;
      this.loading_transactions = false;
    },
    extractHeaders(transactions) {
      if (!transactions.length) return [];

      let headers = Object.keys(transactions[0]).filter((key) =>
        this.deposit_code === "SSD"
          ? key !== "tracking" && key !== "gross"
          : true,
      );

      if (this.deposit_code === "SSD") {
        headers = headers.map((header) =>
          header === "recent_mature_date" ? "Recent Mature Date" : header,
        );
      }

      return headers;
    },
    toTitleCase(str) {
      if (!str) return "";

      return str.replace(
        /([^(]+)(\(([^)]+)\))?/,
        (_, main, parens, insideParens) => {
          const titleMain = main
            .toLowerCase()
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          const titleParens = insideParens
            ? ` (${insideParens
              .toLowerCase()
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" ")})`
            : "";

          return titleMain.trim() + titleParens;
        },
      );
    },
  },
};
</script>

<style scoped>
.papsi-mascot {
  bottom: 0;
  left: 0;
  width: 25rem;
}

.papsi-container {
  height: 50rem;
}

.papsi-text {
  height: 41rem;
  text-align: justify;
}

@media only screen and (min-width: 2500px) {
  .w-48 {
    width: 18rem;
  }
}
</style>
